import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

// NOTA: este es el ÚNICO Historial que usa la app (lo importa App.js desde
// './pages/Historial'). El archivo antiguo src/Historial.js era una copia vieja
// sin botón de descarga y se eliminó.
//
// CAMBIO: se agregó el botón "PDF" junto al de "Excel" para descargar el reporte
// formal en PDF usando el endpoint /conciliaciones/{id}/reporte-pdf del backend.

export default function Historial() {
  const { API, user, logout } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [descargando, setDescargando] = useState(null); // formato "{id}-{tipo}"

  useEffect(() => {
    axios.get(`${API}/historial`)
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  const filtrados = data.filter(r =>
    r.nombre?.toLowerCase().includes(busqueda.toLowerCase()) ||
    r.banco?.toLowerCase().includes(busqueda.toLowerCase()) ||
    r.mes?.toLowerCase().includes(busqueda.toLowerCase())
  );

  const pct = (cruzan, total) => total > 0 ? Math.round(cruzan / total * 100) : 0;

  const fmt = n => {
    const num = parseFloat(n);
    if (!num) return '-';
    return num.toLocaleString('es-CO', { minimumFractionDigits: 0 });
  };

  // tipo: 'excel' | 'pdf'
  const descargarReporte = async (id, banco, mes, tipo) => {
    const clave = `${id}-${tipo}`;
    setDescargando(clave);
    const ruta = tipo === 'pdf'
      ? `${API}/conciliaciones/${id}/reporte-pdf`
      : `${API}/conciliaciones/${id}/reporte`;
    const ext = tipo === 'pdf' ? 'pdf' : 'xlsx';
    try {
      const r = await axios.get(ruta, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Conciliacion_${banco}_${mes}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('No se puede descargar — los datos de esta conciliación no están disponibles.');
    } finally {
      setDescargando(null);
    }
  };

  return (
    <div style={s.page}>
      <nav style={s.nav}>
        <span style={s.navBrand}>🏦 ConciliaApp</span>
        <div style={s.navRight}>
          <a href="/" style={s.navLink}>← Nueva conciliación</a>
          <span style={s.navUser}>👤 {user?.nombre}</span>
          {user?.rol === 'admin' && <a href="/admin" style={s.navLink}>Admin</a>}
          <button onClick={logout} style={s.navBtn}>Salir</button>
        </div>
      </nav>

      <div style={s.content}>
        <div style={s.header}>
          <h2 style={s.title}>📋 Mis conciliaciones</h2>
          <input
            style={s.search}
            placeholder="🔍 Buscar por nombre, banco o mes..."
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
          />
        </div>

        {data.length > 0 && (
          <div style={s.stats}>
            {[
              ['Total', data.length, '#3b82f6'],
              ['Este mes', data.filter(r => {
                const d = new Date(r.created_at);
                const now = new Date();
                return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
              }).length, '#8b5cf6'],
              ['Promedio conciliación', `${Math.round(data.reduce((a, r) => a + pct(r.cruzan, r.total_banco), 0) / data.length)}%`, '#16a34a'],
            ].map(([lbl, val, color]) => (
              <div key={lbl} style={s.stat}>
                <div style={{ ...s.statVal, color }}>{val}</div>
                <div style={s.statLbl}>{lbl}</div>
              </div>
            ))}
          </div>
        )}

        {loading ? (
          <div style={s.empty}>⏳ Cargando...</div>
        ) : filtrados.length === 0 ? (
          <div style={s.empty}>
            <div style={{ fontSize: 48 }}>📭</div>
            <p>{busqueda ? 'No se encontraron resultados' : 'Aún no tienes conciliaciones registradas.'}</p>
          </div>
        ) : (
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  {['Nombre', 'Banco', 'Mes', 'Mov. banco', 'Mov. auxiliar', 'Cruzan', 'Pendientes', '% Conciliado', 'Saldo extracto', 'Saldo libros', 'Fecha', 'Acciones'].map(h => (
                    <th key={h} style={s.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtrados.map(r => {
                  const p = pct(r.cruzan, r.total_banco);
                  return (
                    <tr key={r.id} style={s.tr}>
                      <td style={{ ...s.td, fontWeight: 600 }}>{r.nombre}</td>
                      <td style={s.td}>{r.banco || '-'}</td>
                      <td style={s.td}>{r.mes || '-'}</td>
                      <td style={s.td}>{r.total_banco}</td>
                      <td style={s.td}>{r.total_aux}</td>
                      <td style={{ ...s.td, color: '#16a34a', fontWeight: 600 }}>{r.cruzan}</td>
                      <td style={{ ...s.td, color: r.no_cruzan_banco + r.no_cruzan_aux > 0 ? '#dc2626' : '#16a34a', fontWeight: 600 }}>
                        {r.no_cruzan_banco + r.no_cruzan_aux}
                      </td>
                      <td style={s.td}>
                        <div style={s.pctWrap}>
                          <div style={{ ...s.pctBar, width: `${p}%`, background: p >= 90 ? '#16a34a' : p >= 70 ? '#f59e0b' : '#dc2626' }} />
                          <span style={s.pctTxt}>{p}%</span>
                        </div>
                      </td>
                      <td style={s.td}>{r.saldo_extracto ? `$${fmt(r.saldo_extracto)}` : '-'}</td>
                      <td style={s.td}>{r.saldo_libros ? `$${fmt(r.saldo_libros)}` : '-'}</td>
                      <td style={{ ...s.td, color: '#888', fontSize: 11 }}>
                        {new Date(r.created_at).toLocaleString('es-CO')}
                      </td>
                      <td style={s.td}>
                        <div style={s.acciones}>
                          <button
                            style={{ ...s.btnExcel, opacity: descargando === `${r.id}-excel` ? 0.6 : 1 }}
                            onClick={() => descargarReporte(r.id, r.banco, r.mes, 'excel')}
                            disabled={descargando === `${r.id}-excel`}
                          >
                            {descargando === `${r.id}-excel` ? '⏳' : '📊 Excel'}
                          </button>
                          <button
                            style={{ ...s.btnPdf, opacity: descargando === `${r.id}-pdf` ? 0.6 : 1 }}
                            onClick={() => descargarReporte(r.id, r.banco, r.mes, 'pdf')}
                            disabled={descargando === `${r.id}-pdf`}
                          >
                            {descargando === `${r.id}-pdf` ? '⏳' : '📄 PDF'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#f8fafc', fontFamily: 'system-ui,sans-serif' },
  nav: { background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '0 24px', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  navBrand: { fontWeight: 700, fontSize: 16 },
  navRight: { display: 'flex', alignItems: 'center', gap: 12 },
  navUser: { fontSize: 13, color: '#555' },
  navLink: { fontSize: 13, color: '#2563eb', textDecoration: 'none' },
  navBtn: { padding: '6px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: 'transparent', cursor: 'pointer', fontSize: 13 },
  content: { padding: '24px', maxWidth: 1400, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 20, fontWeight: 700, margin: 0 },
  search: { padding: '10px 16px', borderRadius: 10, border: '1px solid #e5e7eb', fontSize: 14, width: 320, outline: 'none' },
  stats: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 },
  stat: { background: '#fff', borderRadius: 12, padding: '16px', textAlign: 'center', border: '1px solid #e5e7eb' },
  statVal: { fontSize: 28, fontWeight: 700 },
  statLbl: { fontSize: 12, color: '#666', marginTop: 4 },
  empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300, color: '#888', gap: 12, fontSize: 15 },
  tableWrap: { overflowX: 'auto', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { background: '#f8fafc', padding: '10px 14px', textAlign: 'left', fontWeight: 500, color: '#555', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' },
  tr: { transition: 'background 0.15s' },
  td: { padding: '10px 14px', borderBottom: '1px solid #f0f0f0', whiteSpace: 'nowrap' },
  pctWrap: { position: 'relative', height: 20, background: '#f0f0f0', borderRadius: 10, width: 80, overflow: 'hidden' },
  pctBar: { position: 'absolute', left: 0, top: 0, height: '100%', borderRadius: 10, transition: 'width 0.3s' },
  pctTxt: { position: 'absolute', width: '100%', textAlign: 'center', lineHeight: '20px', fontSize: 11, fontWeight: 600, color: '#fff', mixBlendMode: 'difference' },
  acciones: { display: 'flex', gap: 6 },
  btnExcel: { padding: '6px 10px', borderRadius: 8, background: '#16a34a', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  btnPdf: { padding: '6px 10px', borderRadius: 8, background: '#dc2626', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600 },
};
